/**
 * 三方支付供应商（框架通道）配置与回调 endpoint 域 Zod 共享契约（ADM-PAY-S012 / SRV-PAY-038）
 *
 * 契约源：dev-docs `02-API契约/A8-运营支付.md` §10 / §10.1 / §10.1.1 / §10.2 / §10.4（含 dev-docs#1518 补齐的
 * activate 未连接错误、回调登记错误 details 形态与验签材料写入响应字段）+ `03-数据字典/65-三方支付通道.md` §1–§3.1。
 *
 * 本文件只管能力声明读取（§10.0）、配置、连接测试、启停、回调 endpoint 生命周期与验签材料写入；§10.3 通道价格不在本域。
 *
 * 秘密边界：`credential` 与 `material` 是 write-only 输入，服务端 AES-GCM 加密后写入凭据路由 / 验签材料表，
 * 明文与密文都不出任何响应、不进审计与日志、不进幂等指纹明文。响应 DTO 因此不含凭据字段、密文或
 * 内部并发栅栏 `mutation_epoch`。
 */

import { z } from 'zod';

/**
 * 已登记的框架通道代码（`02-API契约/65-三方支付通道框架.md` §2「已登记通道」）。`:channel` 不在本闭集
 * （含 `STRIPE` / `IAP`）时本域全部端点返回 `RESOURCE_NOT_FOUND`（A8 §10 开篇）。
 */
export const PAYMENT_PROVIDER_CHANNELS = ['PAYERMAX'] as const;
export const PaymentProviderChannelSchema = z.enum(PAYMENT_PROVIDER_CHANNELS);
export type PaymentProviderChannel = z.infer<typeof PaymentProviderChannelSchema>;

/** 配置 / 凭据路由 / endpoint 的 `mode`（字典 65 §1–§3）；须与当前部署环境声明一致。 */
export const PAYMENT_PROVIDER_MODES = ['TEST', 'LIVE'] as const;
export const PaymentProviderModeSchema = z.enum(PAYMENT_PROVIDER_MODES);
export type PaymentProviderMode = z.infer<typeof PaymentProviderModeSchema>;

/** 连接状态闭集（字典 65 §1 `connection_status`）。 */
export const PAYMENT_PROVIDER_CONNECTION_STATUSES = ['unverified', 'connected', 'invalid'] as const;
export type PaymentProviderConnectionStatus = (typeof PAYMENT_PROVIDER_CONNECTION_STATUSES)[number];

/**
 * `PAY_PROVIDER_NOT_READY` 的 `issues[].code` 闭集（`01-通用约定/21-Admin错误码段.md` §2.5，dev-docs#1518）。
 * 配置场景（PUT / test / activate，details 带 `app_id`）全集；endpoint 场景（回调登记、验签材料写入，
 * details 为 `{ channel, merchant_account_id, mode }`、不带 `app_id`）只用其中两项。
 */
export const PAYMENT_PROVIDER_CONFIG_NOT_READY_ISSUES = [
  'CONFIG_INCOMPLETE',
  'MODE_MISMATCH',
  'CREDENTIAL_STORE_UNAVAILABLE',
  'CONNECTION_UNVERIFIED',
  'VERIFICATION_MATERIAL_MISSING',
] as const;
export type PaymentProviderConfigNotReadyIssue =
  (typeof PAYMENT_PROVIDER_CONFIG_NOT_READY_ISSUES)[number];
export const PAYMENT_PROVIDER_ENDPOINT_NOT_READY_ISSUES = [
  'MODE_MISMATCH',
  'CREDENTIAL_STORE_UNAVAILABLE',
] as const;
export type PaymentProviderEndpointNotReadyIssue =
  (typeof PAYMENT_PROVIDER_ENDPOINT_NOT_READY_ISSUES)[number];

/** 供应商侧商户账户标识：1..128 字符（A8 §10.1，字典 65 §1 `varchar(128)`）。 */
const MerchantAccountIdSchema = z.string().min(1).max(128);

/**
 * 凭据对象：键集合须**恰为**该通道能力声明的 `credential_fields`（A8 §10.1）。键集合与一致性约束随通道而定，
 * 由服务端按能力声明逐通道校验；此处只挡结构（非空字符串值、单值上限 16 KiB，足够容纳 RSA 4096 PEM）。
 */
const CredentialObjectSchema = z.record(z.string(), z.string().min(1).max(16_384));

/**
 * PUT /api/v1/integrations/payment-providers/:channel/configs/:appId 的 body（A8 §10.1）。
 * `.strict()`：`is_active` / `connection_status` / `config_version` 等服务端管理字段误传即拒，不静默 strip。
 * `native_return_base_url` 的 App 平台分支（原生必填、Web 省略或 null）与 host 登记由服务端判定。
 */
export const UpsertPaymentProviderConfigRequestSchema = z
  .object({
    merchant_account_id: MerchantAccountIdSchema,
    mode: PaymentProviderModeSchema,
    /** write-only；首次创建必填，省略时沿用当前 credential route */
    credential: CredentialObjectSchema.optional(),
    native_return_base_url: z.string().min(1).max(2048).nullable().optional(),
  })
  .strict();
export type UpsertPaymentProviderConfigRequest = z.infer<
  typeof UpsertPaymentProviderConfigRequestSchema
>;

/** POST …/webhook-endpoints 的 body（A8 §10.2）：按商户账户组登记，不含 App。 */
export const RegisterPaymentProviderWebhookEndpointRequestSchema = z
  .object({
    merchant_account_id: MerchantAccountIdSchema,
    mode: PaymentProviderModeSchema,
  })
  .strict();
export type RegisterPaymentProviderWebhookEndpointRequest = z.infer<
  typeof RegisterPaymentProviderWebhookEndpointRequestSchema
>;

/**
 * PUT …/webhook-endpoints/:endpointId/verification-keys 的 body（A8 §10.2）：只有 write-only 的 `material`。
 * `retire_after` 只由 admin 按能力声明求值，请求体携带即被 `.strict()` 拒绝。
 */
export const PutPaymentProviderVerificationKeyRequestSchema = z
  .object({
    material: z.string().min(1).max(16_384),
  })
  .strict();
export type PutPaymentProviderVerificationKeyRequest = z.infer<
  typeof PutPaymentProviderVerificationKeyRequestSchema
>;

/** `ProviderConfigResponse.webhook_endpoints[]` 一项（A8 §10.1）。 */
export type PaymentProviderWebhookEndpointSummary = {
  endpoint_id: string;
  /** 当前环境 API 域 + `/api/v1/webhooks/payment-providers/:endpoint_id` */
  callback_url: string;
  is_active: boolean;
  /**
   * 读取时刻参与验签的材料数（`ACTIVE` 与未到 `retire_after` 的 `RETIRING`），与验签材料写入响应同义；
   * 0 = 该 endpoint 尚无可用验签材料
   */
  verifying_material_count: number;
  /** `retire_after` 最晚的一份 `RETIRING` 材料的该值；没有 `RETIRING` 材料时为 null（与验签材料写入响应同义） */
  latest_retiring_retire_after: string | null;
};

/** 连接测试结果记录的 `status` / `reason` 闭集（A8 §10.1.1「测试结果记录」）。 */
export const PAYMENT_PROVIDER_TEST_RESULT_STATUSES = ['connected', 'invalid'] as const;
export type PaymentProviderTestResultStatus =
  (typeof PAYMENT_PROVIDER_TEST_RESULT_STATUSES)[number];
export const PAYMENT_PROVIDER_TEST_RESULT_REASONS = [
  'CREDENTIAL_INVALID',
  'PERMISSION_DENIED',
] as const;
export type PaymentProviderTestResultReason = (typeof PAYMENT_PROVIDER_TEST_RESULT_REASONS)[number];

/**
 * `ProviderConfigResponse.last_test_result`（A8 §10.1）：本配置行最近一次改变连接状态的测试结果。
 * 不含操作人与请求参数。`config_version` ≠ 当前配置版本时，该结果针对旧版本，页面提示需重新测试。
 */
export type PaymentProviderLastTestResult = {
  status: PaymentProviderTestResultStatus;
  /** `connected` 时为 null */
  reason: PaymentProviderTestResultReason | null;
  /** = 该次测试的 `checked_at` */
  tested_at: string;
  /** 该次测试读取的配置版本（bigint 序列化为 string） */
  config_version: string;
};

/**
 * `ProviderConfigResponse`（A8 §10.1）。字段闭集照契约，**不含**凭据、密文、验签材料、`mutation_epoch`。
 * `webhook_endpoints[]` = 与本配置 channel / merchant_account_id / mode 相同且未退役的 endpoint，
 * `created_at` 升序，含已停用的；同一商户账户组下多个 App 返回同一列表。
 */
export type PaymentProviderConfigResponse = {
  app_id: string;
  channel: PaymentProviderChannel;
  merchant_account_id: string;
  mode: PaymentProviderMode;
  /** Web App 恒 null */
  native_return_base_url: string | null;
  is_active: boolean;
  connection_status: PaymentProviderConnectionStatus;
  last_verified_at: string | null;
  /** bigint 序列化为 string */
  config_version: string;
  webhook_endpoints: PaymentProviderWebhookEndpointSummary[];
  /** 本配置行从未有过测试结果记录时为 null */
  last_test_result: PaymentProviderLastTestResult | null;
};

/**
 * `native_return_base_url` 的 host 不属于当前环境为该 App 登记的支付回跳 host 时，`VALIDATION_FAILED` 的
 * `details.reason`（A8 §10.1）；`details.allowed_hosts` 为当前环境为该 App 登记的 host（无登记时为 `[]`）。
 */
export const PAYMENT_PROVIDER_RETURN_HOST_NOT_REGISTERED = 'RETURN_HOST_NOT_REGISTERED' as const;

/** 能力声明 `currency_rule` 的机读取值闭集（`02-API契约/65-三方支付通道框架.md` §2）。 */
export const PAYMENT_PROVIDER_CURRENCY_RULES = ['SINGLE_PRICE_ROW'] as const;
export type PaymentProviderCurrencyRule = (typeof PAYMENT_PROVIDER_CURRENCY_RULES)[number];

/** 能力声明 `credential_fields` 一项（A8 §10.0）：只描述字段，任何响应都不返回其值。 */
export type PaymentProviderCredentialFieldDescriptor = {
  name: string;
  /** §10.1 PUT 携带 `credential` 时是否必填 */
  required: boolean;
  /** 是否按秘密录入（输入时遮蔽） */
  secret: boolean;
};

/** GET …/:channel/capability 的 `data`（A8 §10.0）：配置页所需的能力声明子集与当前部署环境 mode。 */
export type PaymentProviderCapabilityResponse = {
  channel: PaymentProviderChannel;
  platforms: Array<'IOS' | 'ANDROID' | 'WEB'>;
  price_authority: 'PROVIDER_PRICE' | 'MERCHANT_AMOUNT';
  currency_rule: PaymentProviderCurrencyRule;
  credential_fields: PaymentProviderCredentialFieldDescriptor[];
  /** 当前部署环境声明的 mode，与 §10.1 `mode` 校验同源；环境未声明时为 null（此时 PUT 按 mode 不一致处理） */
  deployment_mode: PaymentProviderMode | null;
};

/** POST …/configs/:appId/test 成功响应（A8 §10.1.1）。 */
export type PaymentProviderConnectionTestResponse = {
  app_id: string;
  channel: PaymentProviderChannel;
  connection_status: 'connected';
  checked_at: string;
};

/** POST …/webhook-endpoints 响应（A8 §10.2）。 */
export type PaymentProviderWebhookEndpointRegistrationResponse = {
  endpoint_id: string;
  callback_url: string;
};

/** endpoint activate / deactivate / retire 响应（A8 §10.2）。 */
export type PaymentProviderWebhookEndpointStateResponse = {
  endpoint_id: string;
  is_active: boolean;
  retired_at: string | null;
};

/** 验签材料写入响应（A8 §10.2，dev-docs#1518）。 */
export type PaymentProviderVerificationKeyWriteResponse = {
  endpoint_id: string;
  /** 写入后当前参与验签的材料数（`ACTIVE` 与未到 `retire_after` 的 `RETIRING`） */
  verifying_material_count: number;
  /** `retire_after` 最晚的一份 `RETIRING` 材料的该值；没有 `RETIRING` 材料时为 null */
  latest_retiring_retire_after: string | null;
};
